import {
  BarChart3Icon,
  BoxIcon,
  BrainIcon,
  CalculatorIcon,
  CalendarXIcon,
  ClipboardListIcon,
  MessageCircleQuestionIcon,
  RulerIcon,
  ShuffleIcon,
  SigmaIcon,
  TrendingDownIcon,
} from 'lucide-react'

export const PRODUCT_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/wkQsRZFejZf9fQPfxFrnm7/455b4ddd-4865-4002-b0e8-ccf46c604756_(1).webp'

export const PRODUCT_MOCKUP_ALT =
  'Das MATHE BEREIT™ Paket: die 6 kompletten Bände für Klasse 1–10, das digitale System auf Desktop, Tablet und Smartphone sowie die 5 enthaltenen Bonus-Pakete.'

export const heroGuarantees = [
  'Sofortiger Download',
  'Kein Abonnement',
  'Lebenslanger Zugriff',
  '7-Tage-Garantie',
]

export const offerGuarantees = [
  'Sofortiger Download',
  'Einmalzahlung',
  'Lebenslanger Zugriff',
  '7-Tage-Garantie',
]

export const stats = [
  { value: '600+', label: 'Sofort einsetzbare Materialien', color: 'text-brand' },
  { value: '1.500+', label: 'Lehrkräfte', color: 'text-leaf' },
  { value: '3.800+', label: 'Gesparte Stunden', color: 'text-flame' },
  { value: '98%', label: 'Empfehlen es weiter', color: 'text-grape' },
]

export const differenceStats = [
  { value: 'Klasse 1–10', label: 'Materialien und Aktivitäten', sub: 'Komplettes System' },
  { value: '6', label: 'Digitale Bände', sub: 'Grundschule + Sek. I' },
  { value: '100%', label: 'Sofortiger Download', sub: 'Direkt nach dem Kauf' },
]

export const beforeItems = [
  'Stunden damit verbringen, jede einzelne Mathematikstunde von Grund auf vorzubereiten und auf verschiedenen Webseiten nach Aufgaben zu suchen.',
  'Arbeitsblätter finden, die nicht genau zum Thema oder zum Lernstand deiner Klasse passen.',
  'Komplexe mathematische Inhalte erklären müssen, ohne eine klare visuelle Unterstützung zu haben.',
  'Schwierigkeiten dabei haben, Aufgaben für unterschiedliche Lernniveaus anzupassen.',
  'Tests, Lösungen und Lernstandschecks jedes Mal selbst erstellen müssen.',
  'Schüler, die abschalten oder zurückbleiben, weil Mathematik für sie kompliziert oder langweilig wirkt.',
]

export const afterItems = [
  '5 Minuten durchsehen und du bist bereit, strukturiert und sicher in den Unterricht zu gehen.',
  'Visuelle Erklärungen, Übungen und Aktivitäten bereits vorbereitet.',
  'Materialien von Klasse 1 bis 10 in einem einzigen System organisiert.',
  'Differenzierungen für unterschiedliche Lernstände bereits integriert.',
  'Lernstandschecks, Lösungen und Lehrerhilfen sofort verfügbar.',
  'Schüler, die Mathematik sehen, verstehen und Schritt für Schritt anwenden können.',
]

export const recognizeItems = [
  {
    icon: MessageCircleQuestionIcon,
    tone: 'bg-cherry/10 text-cherry',
    title: 'Erklärungsstress',
    text: 'Du kommst in den Unterricht und weißt nicht, wie du einen schwierigen mathematischen Inhalt einfach, klar und visuell erklären sollst.',
  },
  {
    icon: CalendarXIcon,
    tone: 'bg-flame/10 text-flame',
    title: 'Verlorene Sonntage',
    text: 'Du verbringst den Sonntag damit, Aufgaben, Arbeitsblätter und Unterrichtsmaterialien vorzubereiten, die eigentlich in wenigen Minuten bereit sein sollten.',
  },
  {
    icon: ShuffleIcon,
    tone: 'bg-grape/10 text-grape',
    title: 'Material-Chaos',
    text: 'Du hast Schwierigkeiten, wirklich hochwertige, visuelle und moderne Mathematikmaterialien zu finden.',
  },
  {
    icon: ClipboardListIcon,
    tone: 'bg-brand/10 text-brand',
    title: 'Endlose Bewertung',
    text: 'Keine klaren Lernstandschecks, Lösungen oder Bewertungsinstrumente, die bereits zu deinen Unterrichtsthemen passen.',
  },
  {
    icon: TrendingDownIcon,
    tone: 'bg-leaf/10 text-leaf',
    title: 'Schüler verlieren den Anschluss',
    text: 'Ein Teil der Klasse bleibt zurück, weil das Material nicht zu ihrem Lernstand passt oder nicht ausreichend differenziert ist.',
  },
]

export const volumes = [
  {
    number: '01',
    title: 'Zahlen & Rechnen',
    subtitle: 'Ein vollständiges System für Zahlenverständnis und Rechenstrategien.',
    icon: CalculatorIcon,
    spine: 'bg-cherry',
    topics: [
      'Zahlen',
      'Addition',
      'Subtraktion',
      'Multiplikation',
      'Division',
      'Stellenwert',
      'Zahlenstrahl',
      'Rechenstrategien',
      'visuelle Übungen',
      'Challenges',
      'Lernstandschecks',
    ],
  },
  {
    number: '02',
    title: 'Geometrie & Raum',
    subtitle: 'Von einfachen Formen bis zu komplexeren geometrischen Zusammenhängen.',
    icon: BoxIcon,
    spine: 'bg-leaf',
    topics: [
      'Formen',
      'Körper',
      'Symmetrie',
      'Winkel',
      'Koordinaten',
      'Umfang',
      'Flächen',
      'Volumen',
      'Kreis',
      'Pythagoras',
      'räumliches Denken',
    ],
  },
  {
    number: '03',
    title: 'Größen, Messen & Einheiten',
    subtitle: 'Mathematik aus dem echten Alltag.',
    icon: RulerIcon,
    spine: 'bg-brand',
    topics: [
      'Längen',
      'Gewichte',
      'Zeit',
      'Geld',
      'Temperatur',
      'Flächenmaße',
      'Volumen',
      'Maßstab',
      'Einheiten',
      'Geschwindigkeit',
      'Umrechnungen',
    ],
  },
  {
    number: '04',
    title: 'Algebra & Gleichungen',
    subtitle: 'Algebra Schritt für Schritt verständlich machen.',
    icon: SigmaIcon,
    spine: 'bg-grape',
    topics: [
      'Muster',
      'Zahlenfolgen',
      'Variablen',
      'Terme',
      'Gleichungen',
      'Ungleichungen',
      'Proportionalität',
      'Dreisatz',
      'Funktionen',
      'Graphen',
    ],
  },
  {
    number: '05',
    title: 'Daten, Statistik & Wahrscheinlichkeit',
    subtitle: 'Daten verstehen, darstellen und interpretieren.',
    icon: BarChart3Icon,
    spine: 'bg-flame',
    topics: [
      'Tabellen',
      'Säulendiagramme',
      'Balkendiagramme',
      'Liniendiagramme',
      'Kreisdiagramme',
      'Mittelwert',
      'Median',
      'Häufigkeiten',
      'Wahrscheinlichkeit',
      'Baumdiagramme',
    ],
  },
  {
    number: '06',
    title: 'Sachaufgaben, Logik & Problemlösen',
    subtitle: 'Mathematik verstehen und wirklich anwenden.',
    icon: BrainIcon,
    spine: 'bg-[#0e7490]',
    topics: [
      'Sachaufgaben',
      'Logikrätsel',
      'mehrstufige Probleme',
      'Strategien',
      'Schätzen',
      'Fehleranalyse',
      'reale Alltagssituationen',
      'mehrere Lösungswege',
      'mathematisches Denken',
    ],
  },
]

export type WorksheetVisualKind =
  | 'tenFrame'
  | 'shapes'
  | 'units'
  | 'fractions'
  | 'balance'
  | 'charts'
  | 'probability'
  | 'steps'

export const pageSamples: Array<{
  level: string
  band: string
  title: string
  caption: string
  visual: WorksheetVisualKind
  header: string
}> = [
  {
    level: 'Klasse 1–4 · Zahlen & Rechnen',
    band: 'Band 01',
    title: 'Die magische 10',
    caption:
      'Visuelle Rechenstrategie mit Zehnerfeld und Zahlenstrahl. Die Schüler sehen Schritt für Schritt, wie eine Aufgabe zerlegt und leichter gelöst werden kann.',
    visual: 'tenFrame',
    header: 'bg-cherry',
  },
  {
    level: 'Klasse 1–4 · Geometrie',
    band: 'Band 02',
    title: 'Formen erkennen und vergleichen',
    caption:
      'Visuelle Übersicht mit Kreis, Dreieck, Quadrat und Rechteck. Die Schüler vergleichen Seiten, Ecken und Eigenschaften.',
    visual: 'shapes',
    header: 'bg-leaf',
  },
  {
    level: 'Klasse 5–6 · Größen & Messen',
    band: 'Band 03',
    title: 'Einheiten richtig umrechnen',
    caption:
      'Visuelle Umrechnungstabelle für Länge, Gewicht und Volumen. Mit klaren Beispielen und praktischen Übungen.',
    visual: 'units',
    header: 'bg-brand',
  },
  {
    level: 'Klasse 5–6 · Brüche',
    band: 'Band 01',
    title: 'Brüche verstehen',
    caption:
      'Kreise, Balkenmodelle und visuelle Beispiele zeigen, was Brüche wirklich bedeuten. Mit Aufgaben und Lösungen.',
    visual: 'fractions',
    header: 'bg-cherry',
  },
  {
    level: 'Klasse 7–10 · Algebra',
    band: 'Band 04',
    title: 'Gleichungen im Gleichgewicht',
    caption:
      'Das Waagenmodell zeigt visuell, warum auf beiden Seiten einer Gleichung dieselbe Operation durchgeführt werden muss.',
    visual: 'balance',
    header: 'bg-grape',
  },
  {
    level: 'Klasse 7–10 · Statistik',
    band: 'Band 05',
    title: 'Diagramme lesen und verstehen',
    caption:
      'Säulen-, Kreis- und Liniendiagramme mit Aufgaben zur Analyse und Interpretation.',
    visual: 'charts',
    header: 'bg-flame',
  },
  {
    level: 'Klasse 7–10 · Wahrscheinlichkeit',
    band: 'Band 05',
    title: 'Wie wahrscheinlich ist es?',
    caption:
      'Würfel, Münzen und Glücksräder machen Wahrscheinlichkeit sichtbar und verständlich.',
    visual: 'probability',
    header: 'bg-flame',
  },
  {
    level: 'Klasse 1–10 · Problemlösen',
    band: 'Band 06',
    title: 'Welche Information brauchst du wirklich?',
    caption:
      'Eine Sachaufgabe wird Schritt für Schritt zerlegt: Lesen, Verstehen, Informationen markieren, Strategie wählen, Rechnen, Prüfen.',
    visual: 'steps',
    header: 'bg-[#0e7490]',
  },
]

export const problemSteps = [
  'Lesen',
  'Verstehen',
  'Informationen markieren',
  'Strategie wählen',
  'Rechnen',
  'Prüfen',
]

export type Bonus = {
  title: string
  was: string
  text: string
  cover: string
  coverLabel: string
  lead?: string
  levels?: Array<{ label: string; dot: string }>
  quote?: string
  steps?: string[]
  features: string[]
}

export const bonuses: Bonus[] = [
  {
    title: 'MATHE BEREIT™ Jahresplaner',
    was: '19 €',
    text: 'Schluss mit chronischer Unordnung und dem Gefühl, mit dem Stoffplan nie fertig zu werden.',
    cover: 'bg-brand',
    coverLabel: 'Jahresplaner',
    features: [
      'Jahresplanung',
      'Monatsplanung',
      'Wochenplanung',
      'Unterrichtsplanung',
      'Themen-Tracker',
      'Prüfungsplanung',
      'Wiederholungsplan',
    ],
  },
  {
    title: '3 Level Differenzierungs-Masterkit',
    was: '27 €',
    text: 'Passe denselben Unterricht innerhalb weniger Sekunden an unterschiedliche Lernstände an.',
    lead: 'Eine Aufgabe. Drei Niveaus.',
    cover: 'bg-leaf',
    coverLabel: 'Differenzierung',
    levels: [
      { label: 'Basis', dot: 'bg-leaf' },
      { label: 'Standard', dot: 'bg-sun' },
      { label: 'Level Up', dot: 'bg-cherry' },
    ],
    features: [],
  },
  {
    title: 'Mathe Starter & Spiele Vault',
    was: '36 €',
    text: 'Schnelle Warm-ups, Spiele, Challenges und Aktivitäten.',
    quote: 'Nie wieder die Frage: „Wie beginne ich heute die Stunde?“',
    cover: 'bg-flame',
    coverLabel: 'Starter & Spiele',
    features: [
      'Zahlenblitz',
      'Mathe-Bingo',
      'Fehler-Challenges',
      'Würfelspiele',
      'Geometrie-Missionen',
      'Algebra-Duelle',
      'Logikrätsel',
      'Statistik-Challenges',
    ],
  },
  {
    title: 'Diagnostik & Lernstand Masterkit',
    was: '17 €',
    text: 'Kurze Lernstandschecks, die dir sofort zeigen, ob deine Schüler wirklich verstanden haben.',
    cover: 'bg-grape',
    coverLabel: 'Diagnostik',
    features: [
      '5-Minuten-Checks',
      'Exit Tickets',
      'Beobachtungsbögen',
      'Fehleranalysen',
      'Themen-Checklisten',
      'Selbsteinschätzung',
    ],
  },
  {
    title: 'Mathe Notfallstunden — Open & Teach',
    was: '17 €',
    text: 'Fertige Mathematikstunden für Tage, an denen keine Vorbereitungszeit bleibt. Mit Lernziel, Aktivität, visueller Erklärung, Challenge, Lösung, Differenzierung und Schnell-Check.',
    cover: 'bg-cherry',
    coverLabel: 'Notfallstunden',
    steps: ['Öffnen.', 'Erklären.', 'Unterrichten.'],
    features: [],
  },
]

export const offerRows = [
  { label: 'MATHE BEREIT™ Pack', detail: '6 digitale Bände', value: '77 €' },
  { label: '5 exklusive strategische Bonus-Pakete', detail: 'Heute gratis', value: '116 €' },
]

export const testimonials = [
  {
    quote:
      'Endlich habe ich meine Abende wieder. Die Materialien sind klar, visuell und ich kann sie direkt im Unterricht einsetzen.',
    name: 'Julia M.',
    location: 'München 🇩🇪',
  },
  {
    quote:
      'Die visuellen Erklärungen helfen meinen Schülern enorm. Besonders die unterschiedlichen Schwierigkeitsstufen sparen mir sehr viel Vorbereitungszeit.',
    name: 'Thomas K.',
    location: 'Hamburg 🇩🇪',
  },
  {
    quote:
      'Von den Grundlagen bis zur Sekundarstufe ist alles strukturiert. Ich muss nicht mehr auf zehn verschiedenen Webseiten nach Material suchen.',
    name: 'Sabine R.',
    location: 'Köln 🇩🇪',
  },
]

export const faqs = [
  {
    q: 'Wie erhalte ich das Material?',
    a: 'Direkt nach dem Kauf erhältst du digitalen Zugriff auf das komplette MATHE BEREIT™ Paket.',
  },
  {
    q: 'Funktioniert es auch für die Sekundarstufe?',
    a: 'Ja. MATHE BEREIT™ deckt Mathematikmaterialien von Klasse 1 bis Klasse 10 ab – von der Grundschule bis zur Sekundarstufe I.',
  },
  {
    q: 'Ist es für unterschiedliche Lernniveaus geeignet?',
    a: 'Ja. Viele Materialien enthalten unterschiedliche Schwierigkeitsstufen, visuelle Hilfen, Basis-Versionen und Level-Up-Aufgaben.',
  },
  {
    q: 'Sind Lösungen enthalten?',
    a: 'Ja. Bei den entsprechenden Übungen, Lernstandschecks und Aufgaben sind Lösungen oder Korrekturhilfen enthalten.',
  },
  {
    q: 'Gibt es monatliche Kosten?',
    a: 'Nein. Es handelt sich um eine einmalige Zahlung von 17,99 €.',
  },
]

export const footerLinks = [
  'Allgemeine Geschäftsbedingungen',
  'Datenschutz',
  'Rückerstattungsrichtlinie',
  'Kontakt',
]
